import { Snowflake, Thermometer } from 'lucide-react';
import type { LotView } from '../api/types.js';
import { formatDateTime, ORIGIN_LABELS, SPACE_LABELS, STATUS_LABELS } from '../lib/format.js';
import { Badge } from './badge.js';
import { DeliveriesTable } from './deliveries-table.js';
import { LineageTreeView } from './lineage-tree.js';

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

function locationLabel(kind: string | null): string {
  return kind === null ? 'Fuera de bodega' : (SPACE_LABELS[kind] ?? kind);
}

export function LotSummary({ lot }: { lot: LotView }) {
  const totals = Object.entries(lot.totalsByStatus);
  return (
    <section
      aria-labelledby="lot-title"
      className="flex flex-col gap-6 rounded-card border border-border bg-card p-6"
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="lot-title" className="text-2xl font-bold break-all">
            {lot.lotId}
          </h2>
          <p className="text-muted-foreground">
            {lot.sku}
            {lot.productName !== null && ` · ${lot.productName}`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge tone="success">{ORIGIN_LABELS[lot.origin] ?? lot.origin}</Badge>
          <Badge tone="accent">
            {lot.isCold ? (
              <Snowflake aria-hidden="true" className="size-4" />
            ) : (
              <Thermometer aria-hidden="true" className="size-4" />
            )}
            {lot.isCold ? 'Refrigerado' : 'Temperatura ambiente'}
          </Badge>
        </div>
      </header>

      {lot.provenancePending === true && (
        <p
          role="status"
          className="rounded-control border border-destructive/40 bg-muted p-4 text-sm"
        >
          El origen de producción de este lote está por confirmar. Sus unidades están bloqueadas
          para venta y acondicionamiento.
        </p>
      )}

      <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Unidades recibidas" value={String(lot.unitsReceived)} />
        <Field
          label="Vencimiento efectivo"
          value={
            lot.effectiveExpiresAt === null
              ? 'Sin unidades en bodega'
              : formatDateTime(lot.effectiveExpiresAt)
          }
        />
        <Field label="Primer registro" value={formatDateTime(lot.firstSeenAt)} />
        <Field
          label="Solicitud de abastecimiento"
          value={lot.supplyRequest === null ? '—' : `#${String(lot.supplyRequest.id)}`}
        />
      </dl>

      <div>
        <h3 className="mb-2 text-lg font-semibold">Estado actual</h3>
        <div className="flex flex-wrap gap-2">
          {totals.map(([status, quantity]) => (
            <Badge key={status}>
              {STATUS_LABELS[status] ?? status}: {quantity}
            </Badge>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <h3 className="mb-2 text-lg font-semibold">Dónde están hoy sus unidades</h3>
        <table className="w-full text-left">
          <caption className="sr-only">Unidades del lote por espacio y estado</caption>
          <thead className="border-b border-border text-sm text-muted-foreground">
            <tr>
              <th scope="col" className="py-2 pr-4 font-medium">
                Espacio
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                Estado
              </th>
              <th scope="col" className="py-2 text-right font-medium">
                Unidades
              </th>
            </tr>
          </thead>
          <tbody>
            {lot.locations.map((location) => (
              <tr
                key={`${location.spaceId ?? 'none'}-${location.status}`}
                className="border-b border-border/60"
              >
                <td className="py-2 pr-4">{locationLabel(location.spaceKind)}</td>
                <td className="py-2 pr-4">{STATUS_LABELS[location.status] ?? location.status}</td>
                <td className="py-2 text-right font-medium">{location.quantity}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <h3 className="mb-2 text-lg font-semibold">Aguas arriba: lotes que lo generaron</h3>
        <LineageTreeView
          rootLotId={lot.lotId}
          tree={lot.upstream}
          direction="upstream"
          emptyMessage={
            lot.origin === 'OWN_PRODUCTION'
              ? 'Aún no se registra el consumo de insumos de este lote.'
              : 'Lote recibido: no proviene de otros lotes nuestros.'
          }
        />
      </div>

      <div>
        <h3 className="mb-2 text-lg font-semibold">Aguas abajo: lotes generados a partir de él</h3>
        <LineageTreeView
          rootLotId={lot.lotId}
          tree={lot.downstream}
          direction="downstream"
          emptyMessage="Todavía no se ha usado en acondicionamientos."
        />
      </div>

      <div>
        <h3 className="mb-2 text-lg font-semibold">Entregas a clientes</h3>
        <DeliveriesTable deliveries={lot.deliveries} />
      </div>
    </section>
  );
}
